<script lang="ts">
	import { onMount, onDestroy } from 'svelte'
	import type { Toast } from '$lib/stores/toast-store'
	import { stopPropagation } from '$lib/utils/clickHelpers'

	interface Props {
		toast: Toast
		ondismiss: (id: string) => void
	}

	let { toast, ondismiss }: Props = $props()
	let show = $state(true)
	let progress = $state(0)
	let previousType = $state(toast.type)

	const DISMISS_DURATION = 5000 // 5 seconds
	let dismissTimeout: ReturnType<typeof setTimeout>
	let progressInterval: ReturnType<typeof setInterval>

	function startProgressAnimation() {
		// Clear any existing timers
		clearTimeout(dismissTimeout)
		clearInterval(progressInterval)

		// Reset progress
		progress = 100

		// Start new progress animation
		const startTime = Date.now()

		// Update progress bar
		progressInterval = setInterval(() => {
			const elapsed = Date.now() - startTime
			progress = Math.max(0, 100 - (elapsed / DISMISS_DURATION) * 100)
		}, 100)

		// Set auto-dismiss timer
		dismissTimeout = setTimeout(() => {
			dismissToast()
		}, DISMISS_DURATION)
	}

	onMount(() => {
		if (toast.type === 'success') {
			startProgressAnimation()
		}
	})

	onDestroy(() => {
		clearTimeout(dismissTimeout)
		clearInterval(progressInterval)
	})

	function dismissToast() {
		show = false
		ondismiss?.(toast.id)
	}

	// Watch for changes in toast type
	$effect(() => {
		if (toast.type !== previousType) {
			previousType = toast.type
			if (toast.type === 'success') {
				startProgressAnimation()
			}
		}
	})

	let toastTypeClasses = $derived(
		{
			success: 'bg-green-50 border-green-200 text-green-800',
			error: 'bg-red-50 border-red-200 text-red-800',
			loading: 'bg-yellow-50 border-yellow-200 text-yellow-800',
		}[toast.type] || 'bg-white border-gray-200 text-gray-800',
	)

	let progressBarColor = $derived(
		{
			success: 'bg-green-500',
			error: 'bg-red-500',
			loading: 'bg-blue-500',
		}[toast.type] || 'bg-gray-500',
	)

	let showProgress = $derived(toast.type !== 'loading')
</script>

{#if show}
	<div class="relative rounded-lg shadow-lg w-full overflow-hidden">
		{#if showProgress}
			<div
				class="absolute top-0 left-0 h-1 {progressBarColor} transition-all duration-100 ease-linear"
				style="width: {progress}%;"
			></div>
		{/if}
		<div
			class="p-2 lg:p-4 border flex items-center justify-between w-full {toastTypeClasses}"
			role="alert"
		>
			<div class="flex items-center gap-3">
				{#if toast.type === 'success'}
					<svg class="w-5 h-5 text-green-500" viewBox="0 0 24 24" fill="none" stroke="currentColor">
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M5 13l4 4L19 7"
						/>
					</svg>
				{:else if toast.type === 'error'}
					<svg class="w-5 h-5 text-red-500" viewBox="0 0 24 24" fill="none" stroke="currentColor">
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
						/>
					</svg>
				{:else if toast.type === 'loading'}
					<svg class="w-5 h-5 text-yellow-800 animate-spin" viewBox="0 0 24 24" fill="none">
						<circle
							class="opacity-25"
							cx="12"
							cy="12"
							r="10"
							stroke="currentColor"
							stroke-width="4"
						/>
						<path
							class="opacity-75"
							fill="currentColor"
							d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
						/>
					</svg>
				{/if}
				<span class="text-sm font-medium">{toast.message}</span>
			</div>

			<button
				class="ml-4 text-sm font-medium opacity-60 hover:opacity-100 transition-opacity touch-manipulation"
				onclick={stopPropagation(dismissToast)}
				aria-label="Dismiss"
			>
				<svg
					class="w-5 h-5"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24"
					xmlns="http://www.w3.org/2000/svg"
				>
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width={2}
						d="M6 18L18 6M6 6l12 12"
					/>
				</svg>
			</button>
		</div>
	</div>
{/if}
